import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { TrainingFollowButton } from './TrainingFollowButton';
import { TrainingFeedCheckin } from '@/hooks/useTrainingFeed';
import { formatDistanceToNow } from 'date-fns';
import { Dumbbell, Utensils, Bed, Film, Brain, Clock } from 'lucide-react';

interface FeedCheckinCardProps {
  checkin: TrainingFeedCheckin;
}

const categoryConfig: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  workout: { icon: Dumbbell, label: 'Workout', color: 'bg-red-500/10 text-red-600' },
  nutrition: { icon: Utensils, label: 'Nutrition', color: 'bg-green-500/10 text-green-600' },
  recovery: { icon: Bed, label: 'Recovery', color: 'bg-blue-500/10 text-blue-600' },
  film_study: { icon: Film, label: 'Film Study', color: 'bg-purple-500/10 text-purple-600' },
  mental: { icon: Brain, label: 'Mental', color: 'bg-amber-500/10 text-amber-600' },
};

export const FeedCheckinCard: React.FC<FeedCheckinCardProps> = ({ checkin }) => {
  const config = categoryConfig[checkin.category] || categoryConfig.workout;
  const CategoryIcon = config.icon;
  const timeAgo = formatDistanceToNow(new Date(checkin.created_at), { addSuffix: true });

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <Link to={`/athletes/${checkin.profile?.username || checkin.profile_id}`}>
              <Avatar className="h-10 w-10">
                <AvatarImage src={checkin.profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(checkin.profile?.name)}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div>
              <Link 
                to={`/athletes/${checkin.profile?.username || checkin.profile_id}`}
                className="font-semibold text-foreground hover:text-primary transition-colors"
              >
                {checkin.profile?.name || 'Athlete'}
              </Link>
              <p className="text-xs text-muted-foreground">{timeAgo}</p>
            </div>
          </div>
          <TrainingFollowButton targetProfileId={checkin.profile_id} />
        </div>

        {/* Category & Title */}
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="secondary" className={`${config.color} font-normal`}>
            <CategoryIcon className="h-3 w-3 mr-1" />
            {config.label}
          </Badge>
          {checkin.duration_minutes && (
            <span className="text-xs text-muted-foreground flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {checkin.duration_minutes} min
            </span>
          )}
        </div>

        <h3 className="font-medium text-foreground mb-2">{checkin.title}</h3>

        {/* Notes preview */}
        {checkin.notes && (
          <p className="text-sm text-muted-foreground line-clamp-2">{checkin.notes}</p>
        )}

        {/* Media thumbnail */}
        {checkin.media_urls && checkin.media_urls.length > 0 && (
          <div className="mt-3">
            <img 
              src={checkin.media_urls[0]} 
              alt="Training media"
              className="rounded-lg w-full max-h-48 object-cover"
            />
            {checkin.media_urls.length > 1 && (
              <p className="text-xs text-muted-foreground mt-1">
                +{checkin.media_urls.length - 1} more
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
